// Business-card extraction service.
//
// Contract (verified against ai-agent-management/business_card/app.py):
//   POST {AIAgentApi root}/card-to-crm/extract
//   header X-Sagelyn-Key  — shared secret, checked with hmac.compare_digest
//                           against the CARD_TO_CRM_SAGELYN_KEY env var
//   body   {"images": [dataUrl, ...]}  — 1..5 images, <= 6MB each
//   200    {"contacts": [{name,title,company,email,phone,mobile,website}]}
//
// The route lives on the same AIAgentApi Gateway as /ai-agents and there is no
// separate env var for that gateway's stage root — deriving it from the agent
// API URL avoids yet another Amplify build var to keep in sync across dev/prod
// (same trick, same reason, as services/contactImportService.ts).
import { getApiBaseUrl } from "../../utils/urlHandler";
import { debug } from "../../utils/debugLogger";
import { Contact, ExtractResponse } from "./types/cardToCrm.types";

const AGENT_API_URL = getApiBaseUrl("REACT_APP_AGENT_API_URL");
const API_ROOT = AGENT_API_URL.replace(/\/ai-agents\/?$/, "");
const API_KEY = (process.env.REACT_APP_BUSINESS_CARD_API_KEY || "").trim();

// Local-dev escape hatch. ai-agent-management/chat-service/local_card_to_crm_api.py
// serves this exact handler on :8020 while the agent shim runs on :8010, so the
// derived root above points at the wrong process for local work. Unset in every
// deployed env (nothing to add to Amplify) — set it only in .env.local.
const EXTRACT_URL_OVERRIDE = (
  process.env.REACT_APP_CARD_EXTRACT_URL || ""
).trim();

const EXTRACT_URL =
  EXTRACT_URL_OVERRIDE || (API_ROOT ? `${API_ROOT}/card-to-crm/extract` : "");

/** Mirrors MAX_IMAGES in business_card/app.py — reject client-side rather than
 *  spend a request (and a rate-limit slot) on a 400. */
export const MAX_IMAGES_PER_EXTRACT = 5;

const newId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Extract contacts from one or more business-card images (base64 data URLs).
 * Returns contacts with a client-generated `id` attached.
 */
export const extractBusinessCards = async (
  images: string[]
): Promise<Contact[]> => {
  if (!EXTRACT_URL) {
    throw new Error(
      "Extraction API is not configured. Set REACT_APP_AGENT_API_URL."
    );
  }
  if (!API_KEY) {
    throw new Error(
      "Extraction API key is missing. Set REACT_APP_BUSINESS_CARD_API_KEY."
    );
  }
  if (images.length > MAX_IMAGES_PER_EXTRACT) {
    throw new Error(
      `At most ${MAX_IMAGES_PER_EXTRACT} cards per scan — remove a few and try again.`
    );
  }

  const response = await fetch(EXTRACT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Sagelyn-Key": API_KEY,
    },
    body: JSON.stringify({ images }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    debug.error("Business card extraction failed:", response.status, errorText);
    if (response.status === 429) {
      // 5 scans/IP/day, shared with the public /card-to-crm page and fail-closed
      // (business_card/app.py). Everyone behind one office IP shares that quota.
      throw new Error(
        "Daily scan limit reached for this network — try again tomorrow."
      );
    }
    throw new Error(
      "We couldn't read your card(s). Please try a clearer photo."
    );
  }

  const data = (await response.json()) as ExtractResponse;
  return (data.contacts || []).map((contact) => ({ ...contact, id: newId() }));
};
